import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { FormField } from '@/components/auth/FormField';
import { SubmitButton } from '@/components/auth/SubmitButton';
import { AuthError } from '@/components/auth/AuthError';
import { supabase } from '@/lib/supabase';
import { describeAuthError } from '@/lib/authErrors';
import { validatePassword, validatePasswordMatch } from '@/lib/validation';

type ReadyState = 'checking' | 'ready' | 'invalid';

export default function ResetPasswordPage(): JSX.Element {
  const navigate = useNavigate();
  const [readyState, setReadyState] = useState<ReadyState>('checking');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ password?: string; confirm?: string }>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Supabase's reset email links to /reset-password#access_token=...&type=recovery.
  // The client picks the tokens up automatically (detectSessionInUrl: true) and
  // emits a PASSWORD_RECOVERY event. We confirm a session exists before
  // showing the form so users can't land here directly and get confused.
  useEffect(() => {
    let cancelled = false;

    async function check(): Promise<void> {
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;
      setReadyState(data.session ? 'ready' : 'invalid');
    }

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;
      if (event === 'PASSWORD_RECOVERY' || session) {
        setReadyState('ready');
      }
    });

    void check();
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setSubmitError(null);

    const errors: { password?: string; confirm?: string } = {};
    const pwError = validatePassword(password);
    if (pwError) errors.password = pwError;
    const matchError = validatePasswordMatch(password, confirm);
    if (matchError) errors.confirm = matchError;

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});
    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) {
        setSubmitError(describeAuthError(error));
        return;
      }
      await supabase.auth.signOut();
      navigate('/login', {
        replace: true,
        state: { successMessage: 'Password updated. Sign in to continue.' },
      });
    } catch (err) {
      setSubmitError(describeAuthError(err));
    } finally {
      setLoading(false);
    }
  }

  if (readyState === 'checking') {
    return (
      <AuthLayout title="Reset password">
        <p className="text-sm text-ink-mid">Verifying your reset link…</p>
      </AuthLayout>
    );
  }

  if (readyState === 'invalid') {
    return (
      <AuthLayout
        title="Reset link invalid"
        footer={
          <Link to="/forgot-password" className="text-ink underline hover:text-candle-dark">
            Request a new link
          </Link>
        }
      >
        <AuthError message="This reset link is invalid or has expired. Request a new one." />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Choose a new password" subtitle="You'll be signed out after saving.">
      <AuthError message={submitError} />

      <form onSubmit={handleSubmit} noValidate>
        <FormField
          name="password"
          label="New password"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          hint="At least 8 characters and one number."
          error={fieldErrors.password}
          required
        />

        <FormField
          name="confirm"
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          error={fieldErrors.confirm}
          required
        />

        <SubmitButton loading={loading}>{loading ? 'Saving…' : 'Save new password'}</SubmitButton>
      </form>
    </AuthLayout>
  );
}
